/**
 * Custom hook for managing AI location recommendations
 * Centralizes state, business logic, and API interactions for AI-suggested locations
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
	addLocationsToMap,
	streamLocationRecommendations,
} from "../../services/dashboards/score-insights.services/ai-locations.services";
import { getLocationId, isLocationAdded } from "../../utils/score-insights/ai/aiLocationMatchers";
import {
	extractLocationsFromSavedAiContent,
	mergeNotesIntoLocations,
	parseNotesFromAiText,
} from "../../utils/score-insights/ai/aiLocationParsers";

/**
 * Hook for managing AI location recommendation state and operations
 * Provides a complete interface for getting, displaying, and adding AI-recommended locations
 *
 */
const useAiLocationRecommendations = ({
	dashboardId,
	dashboardData,
	formData,
	existingLocations,
	token,
	chatFunctions,
	onDashboardRefresh,
}) => {
	// ==================== STATE ====================

	/** AI-recommended locations with notes and scores */
	const [suggestedLocations, setSuggestedLocations] = useState([]);

	/** Locations that have been added to the dashboard from AI suggestions */
	const [addedAiLocations, setAddedAiLocations] = useState([]);

	/** Whether AI has generated recommendations */
	const [hasRecommendation, setHasRecommendation] = useState(false);

	/** Whether AI is currently generating recommendations */
	const [isRecommending, setIsRecommending] = useState(false);

	/** Currently selected location ID (for map interaction) */
	const [selectedLocation, setSelectedLocation] = useState(null);

	/** Currently focused (hovered) location ID */
	const [focusedLocation, setFocusedLocation] = useState(null);

	/** Whether the AI locations modal is visible */
	const [showAiLocations, setShowAiLocations] = useState(false);

	const combinedLocationBounds = useMemo(() => {
		const bounds = [];

		if (existingLocations && existingLocations.length > 0) {
			existingLocations.forEach((loc) => {
				if (loc.lng && loc.lat) bounds.push([loc.lng, loc.lat]);
			});
		}

		if (suggestedLocations && suggestedLocations.length > 0) {
			suggestedLocations.forEach((loc) => {
				if (loc.lng && loc.lat) bounds.push([loc.lng, loc.lat]);
			});
		}

		if (addedAiLocations && addedAiLocations.length > 0) {
			addedAiLocations.forEach((loc) => {
				if (loc.lng && loc.lat) bounds.push([loc.lng, loc.lat]);
			});
		}

		return bounds.length > 0 ? bounds : null;
	}, [existingLocations, suggestedLocations, addedAiLocations]);

	const recommendLocations = useCallback(async () => {
		if (!dashboardData || !dashboardData.requestParams) {
			console.error("Dashboard data not loaded");
			chatFunctions.setError("Dashboard data is not available. Please wait for the dashboard to load.");
			return;
		}

		setIsRecommending(true);
		setSuggestedLocations([]);
		setHasRecommendation(false);

		try {
			const result = await streamLocationRecommendations({
				dashboardData: {
					type: "score_insights",
					dashboard_type: "score_insights",
					dashboard_id: dashboardData.requestParams.dashboard,
					soc_codes: dashboardData.requestParams.socCodes,
					location_ids: existingLocations.map((loc) => getLocationId(loc)),
					target_wage: dashboardData.requestParams.target_wage,
					drive_time: formData.driveTime || dashboardData.requestParams.drive_time,
					soc_occupation_quantities: dashboardData.requestParams.soc_occupation_quantities,
				},
				token: token,
				chatFunctions: chatFunctions,
			});

			// Update state with results
			if (result.locations && result.locations.length > 0) {
				setSuggestedLocations(result.locations);
				setHasRecommendation(true);

				// Refresh dashboard data so the backend's has_ai_note flag is picked up.

				if (onDashboardRefresh) {
					onDashboardRefresh();
				}
			}
		} catch (error) {
			console.error("Error recommending location:", error);
			chatFunctions.setError(error.message || "An error occurred while getting recommendations");
		} finally {
			setIsRecommending(false);
		}
	}, [dashboardData, existingLocations, token, chatFunctions]);

	/**
	 * Add a single AI-recommended location to the dashboard
	 * Updates backend and local state
	 *
	 */
	const addLocation = useCallback(
		async (location) => {
			try {
				const locationId = getLocationId(location);
				if (!locationId) {
					console.warn("Cannot add location without valid ID", location);
					return;
				}

				const alreadyAdded = isLocationAdded(location, addedAiLocations);
				if (alreadyAdded) {
					console.log("Location already added", locationId);
					return;
				}

				await addLocationsToMap({
					dashboardId: dashboardId,
					locations: [location],
				});

				setAddedAiLocations((prev) => [
					...prev,
					{
						...location,
						_id: locationId,
					},
				]);

				setSuggestedLocations((prev) => prev.filter((loc) => getLocationId(loc) !== locationId));

				if (onDashboardRefresh) {
					onDashboardRefresh();
				}
			} catch (error) {
				console.error("Error adding location to dashboard:", error);
				throw error;
			}
		},
		[dashboardId, addedAiLocations, onDashboardRefresh],
	);

	/**
	 * Add all suggested locations to the dashboard at once
	 * Filters out locations that are already added
	 */
	const addAllLocations = useCallback(async () => {
		try {
			// Filter out locations that are already added
			const newLocations = suggestedLocations.filter(
				(suggestedLoc) => !isLocationAdded(suggestedLoc, addedAiLocations),
			);

			if (newLocations.length === 0) {
				console.log("All locations already added");
				return;
			}

			await addLocationsToMap({
				dashboardId: dashboardId,
				locations: newLocations,
			});

			setAddedAiLocations((prev) => [...prev, ...newLocations]);

			setSuggestedLocations((prev) =>
				prev.filter((loc) => !newLocations.some((added) => getLocationId(added) === getLocationId(loc))),
			);

			if (onDashboardRefresh) {
				onDashboardRefresh();
			}
		} catch (error) {
			console.error("Error adding all locations to dashboard:", error);
			throw error;
		}
	}, [dashboardId, suggestedLocations, addedAiLocations, onDashboardRefresh]);

	/**
	 * Show the AI locations modal
	 */
	const showLocationsModal = useCallback(() => {
		setShowAiLocations(true);
	}, []);

	/**
	 * Hide the AI locations modal and reset selection state
	 */
	const hideLocationsModal = useCallback(() => {
		setShowAiLocations(false);
		setSelectedLocation(null);
		setFocusedLocation(null);
	}, []);

	// ==================== EFFECTS ====================

	/**
	 * Syncs local state with backend data
	 */
	useEffect(() => {
		if (!dashboardData?.locations) return;

		const aiAdded = dashboardData.locations.filter((loc) => loc.is_added_from_ai === true);

		if (aiAdded.length === 0) return;

		setAddedAiLocations(
			aiAdded.map((loc) => ({
				...loc,
				_id: loc._id || loc.location_id,
			})),
		);
	}, [dashboardData?.locations]);

	/**
	 * Only runs if dashboard has saved AI note and suggested locations haven't been loaded yet
	 */
	useEffect(() => {
		if (!dashboardData?.has_ai_note) return;

		const savedContent = dashboardData?.ai_suggestion_saved_response?.assistant_content;
		if (!savedContent) return;

		if (suggestedLocations.length > 0) return;

		const extractedLocations = extractLocationsFromSavedAiContent(savedContent);
		if (extractedLocations.length === 0) return;

		const parsedNotes = parseNotesFromAiText(savedContent);

		const mergedLocations = mergeNotesIntoLocations(extractedLocations, parsedNotes);

		setSuggestedLocations(
			mergedLocations.map((loc) => ({
				...loc,
				_id: loc._id || loc.location_id,
			})),
		);
	}, [
		dashboardData?.has_ai_note,
		dashboardData?.ai_suggestion_saved_response?.assistant_content,
		suggestedLocations.length,
	]);

	return {
		suggestedLocations,
		addedAiLocations,
		hasRecommendation,
		isRecommending,
		selectedLocation,
		focusedLocation,
		showAiLocations,

		combinedLocationBounds,

		recommendLocations,
		addLocation,
		addAllLocations,
		showLocationsModal,
		hideLocationsModal,
	};
};

export default useAiLocationRecommendations;
